import { useGoogleMap } from '@react-google-maps/api';
import { useEffect, useRef } from 'react';

// Status-Farben. `text` wird für die Labels verwendet — auf hellen Hintergründen
// (Gelb für "waiting") braucht es dunkle Schrift, damit die Nummer lesbar bleibt.
const STATUS_COLORS = {
  available:     { bg: '#22C55E', border: '#16A34A', pulse: '#22C55E', text: '#ffffff' },
  en_route:      { bg: '#F59E0B', border: '#D97706', pulse: '#F59E0B', text: '#ffffff' },
  waiting:       { bg: '#FBC02D', border: '#F9A825', pulse: '#FBC02D', text: 'rgba(0,0,0,0.87)' },
  with_customer: { bg: '#EF4444', border: '#DC2626', pulse: '#EF4444', text: '#ffffff' },
  paused:        { bg: '#3B82F6', border: '#2563EB', pulse: '#3B82F6', text: '#ffffff' },
  offline:       { bg: '#9CA3AF', border: '#6B7280', pulse: '#9CA3AF', text: '#ffffff' },
  pickup:        { bg: '#22C55E', border: '#15803D', pulse: '#22C55E', text: '#ffffff' },
  destination:   { bg: '#1D4ED8', border: '#1E3A8A', pulse: '#1D4ED8', text: '#ffffff' },
  // Zwischenstopp: eigene Farbe, damit er sich vom Endziel abhebt; ohne SVG,
  // damit die Halt-Nummer (`label`) sichtbar bleibt.
  stop:          { bg: '#7C3AED', border: '#5B21B6', pulse: '#7C3AED', text: '#ffffff' },
};

// SVG-Inhalt pro Marker-Typ; null bedeutet "Taxi-Standard-Icon".
const STATUS_SVG = {
  pickup: '<svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z"/></svg>',
  destination: '<svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff"><path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6h-5.6z"/></svg>',
};

// CSS für Pulsier-Animation (wird einmalig injected)
let styleInjected = false;
const injectStyles = () => {
  if (styleInjected) return;
  styleInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes marker-pulse {
      0% { transform: scale(1); opacity: 0.6; }
      50% { transform: scale(1.8); opacity: 0; }
      100% { transform: scale(1); opacity: 0; }
    }
    .ncb-marker { transition: transform 0.2s ease; }
    .ncb-marker:hover { transform: scale(1.15); }
  `;
  document.head.appendChild(style);
};

/**
 * Erstellt ein Custom-HTML-Element als Marker-Content
 */
const createCustomMarkerContent = ({ status, label, isRealtime }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.offline;
  const displayLabel = label ? String(label) : '';
  const labelLength = displayLabel.length;
  const fontSize = labelLength > 4 ? 10 : labelLength > 2 ? 12 : 13;

  const container = document.createElement('div');
  container.className = 'ncb-marker';
  container.style.cssText = 'position:relative;cursor:pointer;';

  // Pulsier-Ring für Live-Fahrer
  if (isRealtime && status !== 'offline') {
    const pulse = document.createElement('div');
    pulse.style.cssText = `
      position:absolute;top:50%;left:50%;
      width:40px;height:40px;
      margin-top:-20px;margin-left:-20px;
      border-radius:50%;
      background:${colors.pulse};
      animation:marker-pulse 2s ease-out infinite;
      pointer-events:none;
    `;
    container.appendChild(pulse);
  }

  // Hauptkreis
  const circle = document.createElement('div');
  circle.style.cssText = `
    position:relative;
    min-width:36px;height:36px;
    padding:0 6px;
    box-sizing:border-box;
    border-radius:18px;
    background:${colors.bg};
    border:3px solid ${colors.border};
    display:flex;align-items:center;justify-content:center;
    box-shadow:0 2px 8px rgba(0,0,0,0.3);
    color:${colors.text || '#ffffff'};font-weight:700;font-size:${fontSize}px;
    font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
    text-transform:uppercase;
    letter-spacing:0;
    white-space:nowrap;
  `;

  // Inhalt: Status-spezifisches SVG (z.B. Pickup/Destination), sonst Label, sonst Taxi-Fallback.
  if (STATUS_SVG[status]) {
    circle.innerHTML = STATUS_SVG[status];
  } else if (displayLabel) {
    circle.textContent = displayLabel;
  } else {
    // Kleines Taxi-SVG als Fallback — fill folgt der Statustextfarbe.
    circle.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="${colors.text || '#ffffff'}">
      <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
    </svg>`;
  }
  container.appendChild(circle);

  // Kleiner Pfeil nach unten (Zeiger)
  const arrow = document.createElement('div');
  arrow.style.cssText = `
    width:0;height:0;
    border-left:6px solid transparent;
    border-right:6px solid transparent;
    border-top:8px solid ${colors.border};
    margin:0 auto;
    margin-top:-1px;
  `;
  container.appendChild(arrow);

  return container;
};

const buildPinFromIcon = (icon) => {
  if (!icon) return null;
  const background = icon.url
    ? icon.url.includes('green') ? '#22C55E'
      : icon.url.includes('blue') ? '#3B82F6'
      : icon.url.includes('red') ? '#EF4444'
      : icon.url.includes('gray') ? '#9CA3AF'
      : '#F59E0B'
    : '#F59E0B';
  return new window.google.maps.marker.PinElement({
    scale: icon.scaledSize ? (icon.scaledSize.width / 32) : 1,
    background,
  }).element;
};

/**
 * AdvancedMarker-Komponente mit Custom HTML Content
 *
 * Der Marker wird pro Map-Instanz genau einmal erstellt. Position, Inhalt,
 * Titel und zIndex werden anschließend über Property-Updates aktualisiert,
 * damit Echtzeit-Positions-Updates kein Flackern verursachen.
 */
function AdvancedMarker({
  position,
  title,
  icon,
  status,
  label,
  isRealtime,
  onClick,
  zIndex
}) {
  const mapInstance = useGoogleMap();
  const markerRef = useRef(null);
  const onClickRef = useRef(onClick);
  const isAdvancedRef = useRef(false);

  // onClick stets aktuell halten, ohne den Marker neu zu erzeugen
  useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  useEffect(() => {
    injectStyles();
  }, []);

  // Marker einmalig pro Map-Instanz erstellen
  useEffect(() => {
    if (!mapInstance || !window.google) return undefined;

    let cancelled = false;
    let retries = 0;
    let retryTimeout = null;

    const handleClick = () => {
      if (onClickRef.current) onClickRef.current();
    };

    const createMarker = async () => {
      if (cancelled) return;
      let isAdvancedAvailable =
        window.google.maps.marker &&
        typeof window.google.maps.marker.AdvancedMarkerElement === 'function';

      if (!isAdvancedAvailable && typeof window.google.maps.importLibrary === 'function') {
        try {
          await window.google.maps.importLibrary('marker');
          isAdvancedAvailable =
            window.google.maps.marker &&
            typeof window.google.maps.marker.AdvancedMarkerElement === 'function';
        } catch (error) {
          console.warn('AdvancedMarker: marker library konnte nicht geladen werden:', error);
        }
      }

      if (cancelled) return;

      if (isAdvancedAvailable) {
        const options = {
          position,
          title,
          map: mapInstance,
          zIndex,
        };
        if (status) {
          options.content = createCustomMarkerContent({ status, label, isRealtime });
        } else {
          const pinElement = buildPinFromIcon(icon);
          if (pinElement) options.content = pinElement;
        }

        const marker = new window.google.maps.marker.AdvancedMarkerElement(options);
        marker.addListener('gmp-click', handleClick);
        markerRef.current = marker;
        isAdvancedRef.current = true;
        return;
      }

      if (retries < 20) {
        retries += 1;
        retryTimeout = setTimeout(createMarker, 250);
        return;
      }

      // Fallback: Legacy Marker
      const marker = new window.google.maps.Marker({
        position,
        title,
        icon,
        map: mapInstance,
        zIndex,
      });
      marker.addListener('click', handleClick);
      markerRef.current = marker;
      isAdvancedRef.current = false;
    };

    createMarker();

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (markerRef.current) {
        if (typeof markerRef.current.setMap === 'function') {
          markerRef.current.setMap(null);
        } else {
          markerRef.current.map = null;
        }
        markerRef.current = null;
      }
    };
    // Create-Effect läuft nur bei neuer Map-Instanz; Folge-Updates via
    // property-assignment weiter unten, damit keine DOM-Neuerstellung nötig ist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInstance]);

  // Position aktualisieren, ohne den Marker neu zu erstellen
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || !position) return;
    if (isAdvancedRef.current) {
      marker.position = position;
    } else if (typeof marker.setPosition === 'function') {
      marker.setPosition(position);
    }
  }, [position]);

  // Inhalt (Status/Label/Realtime) aktualisieren
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || !isAdvancedRef.current) return;
    if (status) {
      marker.content = createCustomMarkerContent({ status, label, isRealtime });
    }
  }, [status, label, isRealtime]);

  // Titel aktualisieren
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;
    if (isAdvancedRef.current) {
      marker.title = title || '';
    } else if (typeof marker.setTitle === 'function') {
      marker.setTitle(title || '');
    }
  }, [title]);

  // zIndex aktualisieren
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || zIndex == null) return;
    if (isAdvancedRef.current) {
      marker.zIndex = zIndex;
    } else if (typeof marker.setZIndex === 'function') {
      marker.setZIndex(zIndex);
    }
  }, [zIndex]);

  return null;
}

export default AdvancedMarker;
